import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import EmptyLayout from "layouts/empty-layout";
import { SidebarMenu } from "components/global/tabselector";
import { TabPanel, useTabs } from "react-headless-tabs";
import TemplatePanel from "./panels/template";
import PersonalPanel from "./panels/personal";
import MoreInfoPanel from "./panels/moreinfo";
import MilestonePanel from "./panels/milestone";
import QRCodePanel from "./panels/qrcode";
import ShareDownloadPanel from "./panels/share";
import MoreInfoPanelTG from "./panels/moreinfo-tg";
import ResumePreview from "./preview/preview";
import _ from "lodash";
import { useUser } from "redux/modules/user/hooks";
import { useResume } from "redux/modules/resume/hooks";
import { resumeLinkId, randomExtension, parseDateOnly } from "utils/utils";
import { filterPrivateMilestones } from "./resume";
import toast from "react-hot-toast";
import { Context } from "context/context";
import useDebounce from "utils/customhooks";
import ResumeSideBarMobile from "./sidebar-mobile";
import { useTranslation } from "react-i18next";
import { LangContext } from "context/language";
import { useProfileGallery } from "redux/modules/profile-gallery/hooks";
import useProfileGalleryHook from "utils/useProfileGaller";
import Favicon from "components/fav-icon/Favicon";
import PreviewPDF from "./preview/previewPDF";

// export const sideBarMenuList = [
//   {
//     label: "Template",
//     value: "template",
//   },
//   {
//     label: "Personal Info",
//     value: "personal",
//   },
//   {
//     label: "More Info",
//     value: "more",
//   },
//   {
//     label: "Milestones",
//     value: "milestone",
//   },
//   {
//     label: "Share/Download",
//     value: "download",
//   },
// ];

export default function ResumeBuilder(props) {
  const { http } = global.services;
  const history = useHistory();
  const query = useQuery();
  const isEdit = query && query.isEdit ? true : false;
  const resumeDetails = isEdit ? query.resumeDetails : null;
  const profile = JSON.parse(localStorage.getItem('user_data'))?.profile;
  const {t} = useTranslation();
  const {sideBarMenuList} = useContext(LangContext);
  const {
    state,
    setState,
    milestones,
    setMilestones,
    previewImage,
    setPreviewImage,
  } = useContext(Context);

  const previewRef = useRef()
  const {setPhotos:setProfileGallery, initialize:initializeProfileGallery,
    total:totalPG,
    limit:limitPG,
    finishGetPhoto,
    isRequesting: isRequestingPG,
    loadMore: loadMorePG
  } = useProfileGallery();
  const extension = randomExtension();
  const { userData, socialMedia = [], setSocialMedia } = useUser();
  const { addResume, updateResume,setBioResume, display_qr_reels } = useResume();
  const [page, setPage] = useState(1);
  const [selectedTab, setSelectedTab] = useTabs([
    "template",
    "personal",
    "more",
    "milestone",
    "qrcode",
    "download",
  ]);
  const [initialMount, setInitialMount] = useState(true);
  const [isSubmit, setIsSubmit] = useState(false);
  const [showMobileSideBar, setShowMobileSideBar] = useState(false);

  const debounce = useDebounce(state, 2000);
  const debounceMilestone = useDebounce(milestones, 2000);

  const linkId = resumeLinkId();

  const [primaryColor, secondaryColor, tertiaryColor, quarternary] =
    useMemo(() => {
      if (!_.isEmpty(state.color)) {
        return state.color.value;
      }
      return ["", "", "", ""];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.color]);

  const {getProfileGallery} = useProfileGalleryHook({
    userID : userData?.profile.id, isRequestingPG, loadMorePG,
    totalPG, limitPG, setProfileGallery, finishGetPhoto,http
  })

    useEffect(() => {
      if (initializeProfileGallery) getProfileGallery();
    }, [initializeProfileGallery,getProfileGallery]);

  /******** functions ********/
  const onChangeColor = useCallback((color) => {
    setState((prev) => ({
      ...prev,
      color,
    }));
    if (isEdit) {
      setInitialMount(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeTemplate = useCallback((name) => {
    setState((prev) => ({
      ...prev,
      templateName: name,
    }));

    setSelectedTab("personal");
    if (isEdit) {
      setInitialMount(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeToggleImage = useCallback((value) => {
    setState((prev) => ({
      ...prev,
      show_image: value,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeToggleImageMilestone = useCallback((value) => {
    setState((prev) => ({
      ...prev,
      show_milestone_image: value,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show/hide private milestones on the resume (display only; the saved
  // selection is unchanged). (Marker.io MCF-1827)
  const onChangeTogglePrivateMilestone = useCallback((value) => {
    setState((prev) => ({
      ...prev,
      show_private_milestone: value,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeToggleQRCode = useCallback((value,type) => {
    setState((prev) => ({
      ...prev,
      [type]: value,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A showreel QR only makes sense when the profile actually has a showreel
  // video (display_qr_reels comes from the API). If there is none, force the
  // toggle off so no unusable QR is rendered in the preview or saved with the
  // resume (Marker.io MCF-1742 — a QR pointing to an empty showreel).
  useEffect(() => {
    if (!display_qr_reels && state.show_qr_reels) {
      setState((prev) => ({ ...prev, show_qr_reels: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [display_qr_reels, state.show_qr_reels]);

  const onChangePersonalInfo = useCallback(
    (personal) => {
      let personalData = {};

      personal.forEach((s) => {
        personalData[s.key] = s.value;
        if (s.key === "profile_image" && typeof s.value === "object") {
          setPreviewImage(URL.createObjectURL(s.value));
        } else if (s.key === "profile_image") {
          setPreviewImage(s.value);
        }
      });

      setState((prev) => ({
        ...prev,
        personal: personalData
      }));
      setInitialMount(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onChangeHideOccp = useCallback(
    (hideOccupation=[]) => {
      setState((prev) => ({
        ...prev,
        hideOccupation:hideOccupation
      }));
      setInitialMount(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onChangeHidePosition = useCallback(
    (milestone_position={}) => {
      setState((prev) => ({
        ...prev,
        milestone_position:milestone_position
      }));
      setInitialMount(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onChangeSocialInfo = useCallback(
    (social) => {
      let socialData = [];

      social.forEach((s) => {
        // socialData[s.key] = s.value;
        socialData.push({
          id:s?.id,
          label:s?.label,
          value:s?.value,
          icon:s?.icon || null
        })
      });

      setState((prev) => ({
        ...prev,
        social: socialData,
      }));

      setInitialMount(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onChangeMilestones = useCallback((key, value) => {
    setMilestones((prev) => ({
      ...prev,
      [key]: value,
    }));

    setInitialMount(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNext = () => {
    if (selectedTab === "personal") {
      setPage(1);
      setSelectedTab("more");
    } else if (selectedTab === "more") {
      setPage(2);
      setSelectedTab("milestone");
    } else if (selectedTab === "milestone") {
      setPage(2);
      setSelectedTab("qrcode");
    } else if (selectedTab === "qrcode") {
      setPage(2);
      setSelectedTab("download");
    }
  };

  const onBack = () => {
    if (selectedTab === "personal") {
      setPage(1);
      setSelectedTab("template");
    } else if (selectedTab === "more") {
      setPage(1);
      setSelectedTab("personal");
    } else if (selectedTab === "milestone") {
      setPage(1);
      setSelectedTab("more");
    } else if (selectedTab === "qrcode") {
      setPage(2);
      setSelectedTab("milestone");
    } else if (selectedTab === "download") {
      setPage(2);
      setSelectedTab("qrcode");
    }
  };

  /** submit resume for creation */
  const onUpdateResume = async () => {
    // Never save without a template. This runs on an autosave, and while the
    // form only sends template_name when it is truthy, ResumeController writes
    // 'template_name' => $request->template_name unconditionally — so an absent
    // field lands as NULL and the user loses their template for good. A resume
    // is meaningless without one, so there is nothing worth saving in this state.
    if (!state.templateName) return;

    setIsSubmit(true);
    let formData = new FormData();
    if (state.id) {
      formData.append("user_id", userData.user.id);
      formData.append("link_id", state.link_id);
    } else {
      formData.append("user_id", userData.user.id);
      formData.append("user_profile_id", userData.profile.id);
      formData.append("link_id", linkId);
    }
    // name
    if (state.templateName)
      formData.append("template_name", state.templateName);
    // color
    if (!_.isEmpty(state.color))
      formData.append("template_color", JSON.stringify(state.color));
    // personal
    if (!_.isEmpty(state.personal))
      formData.append("personal_info", JSON.stringify({...state.personal, profile_type_sub: profile?.profile_type?.type || 'personal',hideOccupation:[...state.hideOccupation]}));
    // more info
    if (!_.isEmpty(state.more)){
      // Store the bio in full. This runs on an autosave keyed to `debounce`, so
      // truncating here cut the user's text mid-edit: the API echoed the short
      // bio back, updateResume refreshed `resumes`, and moreinfo's re-seed effect
      // pushed it into the editor about a second after they typed. The limit is
      // applied where it belongs — at preview and PDF render, via bio-limit.
      let moreInfo = state.more;
      formData.append("more_info", JSON.stringify(moreInfo));
    }
    // social
    if (!_.isEmpty(state.social))
      formData.append("social_media", JSON.stringify(state.social));
    // profile
    if (state?.personal?.profile_image)
      formData.append("profile_image", state.personal.profile_image);
    //show_milestone_image
    formData.append("show_milestone_image", state.show_milestone_image ? 1 : 0);
    formData.append("show_private_milestone", state.show_private_milestone ? 1 : 0);
    formData.append("show_qr_milestone", state.show_qr_milestone ? 1 : 0);
    formData.append("show_qr_reels", state.show_qr_reels ? 1 : 0);
    formData.append("milestone_position", JSON.stringify(state.milestone_position));

    let _featured = [];
    let _current = [];
    let _past = [];

    Object.keys(milestones).forEach((key) => {
      milestones[key]?.forEach?.((m) => {
        if (key === "featured") {
          _featured.push(m?.id);
        } else if (key === "current") {
          _current.push(m?.id);
        } else {
          _past.push(m?.id);
        }
      });
    });

    formData.append("featured", JSON.stringify(_featured));
    formData.append("current", JSON.stringify(_current));
    formData.append("past", JSON.stringify(_past));

  if (state.id) {
      formData.append("_method", "PUT");
    } 
  let template_image = state.template_url;
  // if(!template_image){
  //       template_image = await previewRef.current.handleCapture();
  //       formData.append("template_image",template_image);
  //   }

    try {
      const url = state.id ? `public/resumes/${state.id}` : "public/resumes";

      const { data } = await http.post(url, formData, {
        headers: {
          "content-type": "multipart/form-data",
        },
      });
      console.log(data.data)
      if (state.id) {
        updateResume(data.data);
      } else {
        addResume(data.data);
        setState((prev) => ({
          ...prev,
          id: data.data.id,
          link_id: data.data.link_id,
          template_url: template_image,
        }));
      }
    } catch (err) {
      setIsSubmit(false);
    } finally {
      setIsSubmit(false);
    }
  };

  const onDone = () => {
    history.push(
      `/${userData.user.username}-${extension}/profile/${userData.profile.id}`
    );
  };

  const getSocialMedia = async () => {
    try {
      const { data } = await http.get("public/social-medias", {
        user_profile_id: userData?.profile?.id,
        user_id: userData?.user?.id,
      });
      setSocialMedia(data.social_media);
    } catch (err) {
      console.log(err);
    }
  };

  const getInfo = async (profile, user) => {
    try {
      const { data } = await http.get(`public/professional-informations`, {
        user_profile_id: profile.id,
      });
      let professional = {
        education: "",
        training: "",
        experience: "",
        skills: "",
      };
      if (!_.isEmpty(data)) {
        //update state to false
        professional = {
          education: state.more?.education ?? data.education ?? "",
          training: state.more?.training ?? data.training ?? "",
          experience: state.more?.experience ?? data.experience ?? "",
          awards: state.more?.awards ?? data.awards ?? "",
          representation: state.more?.representation ?? data.representation ?? "",
          skills: state.more?.skills ?? data.skills ?? ""
        };
      }
      setState((prev) => ({
        ...prev,
        more: {
          username: prev.more?.username ?? user.username,
          bio: state?.id ? prev.more?.bio : profile.bio,
          birthdate: prev.more?.birthdate ?? (profile?.birthdate ? parseDateOnly(profile?.birthdate) : null),
          birth_location: prev.more?.birth_location || profile.birth_location,
          civil_status: prev.more?.civil_status || profile.civil_status,
          children: prev.more?.children || profile.children,
          other_name: prev.more?.other_name || profile.other_name,
          hair_color: prev.more?.hair_color || profile.hair_color,
          eye_color: prev.more?.eye_color || profile.eye_color,
          height: prev.more?.height || profile.height,
          phone_number: prev.more?.phone_number || profile.phone_number,
          email: prev.more?.email || user.email,
          occupations: prev.more?.occupations ?? profile?.occupations ?? [],
          mentions: prev?.mentions || profile?.bio_mentions || [],
          ...professional,
        },
      }));
      setBioResume(state?.id ? state.more?.bio : profile.bio)
    } catch (error) {
      console.error("Error Fecthing Professional Information");
    }
  };

  const onChangeTabs = useCallback(
    (name) => {
      if (state.templateName) {
        if (name === "milestone" || name === "qrcode") {
          setPage(2);
        } else {
          setPage(1);
        }
        setSelectedTab(name);
      } else {
        toast.error("Please Select Template");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  /******** useEffects ********/
  useEffect(() => {
    if (userData) {
      const { profile, user } = userData;
      getInfo(profile, user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (userData) {
      getSocialMedia();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  useEffect(() => {
    if (!initialMount) {
      onUpdateResume();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounce, debounceMilestone]);

  return (
    <EmptyLayout
      backButton = {t('close')}
      description={t('buildYourResume')}
      title={t('resumeBuilder')}
      isOpen={showMobileSideBar}
      onClickMobileSideBar={() => setShowMobileSideBar((prev) => !prev)}>
      <div className="w-full h-full flex flex-row relative">
        <ResumeSideBarMobile
          isOpen={showMobileSideBar}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          onChangeTabs={(value) => onChangeTabs(value)}
        />
        {/* sidebar */}
        <div className="w-80.1 h-full py-6.5 pl-6.4 pr-5 border-r border-resumeBorder hidden 3md:block">
          <div className="w-full grid gap-y-1 ">
            {sideBarMenuList.map((obj, index) => (
              <SidebarMenu
                key={index}
                isActive={selectedTab === obj.value}
                colorType="blue"
                onClick={() => onChangeTabs(obj.value)}>
                <div className="w-full flex flex-row justify-start items-center space-x-3">
                  <div className="relative w-6 h-6 rounded-full border-2 resume-side-number">
                    {index + 1}
                  </div>
                  <span>{obj.label}</span>
                </div>
              </SidebarMenu>
            ))}
          </div>
        </div>
        {/* end: sidebar */}
        {/* begin tabs */}
        <div className="h-full overflow-y-auto overflow-x-hidden custom-scroll resume-builder-width-responsive">
          <TabPanel hidden={selectedTab !== "template"}>
            <TemplatePanel
              {...{
                onChangeColor,
                onChangeTemplate,
                color: state.color,
                // onChangeToggleImage,
                show_image: state.show_image,
                profileType: profile?.profile_type?.type,
              }}
            />
          </TabPanel>
          {selectedTab !== "template" && (
            <div className="w-full" style={{ height: "calc(100vh - 80px)" }}>
              <div className="w-full h-full grid grid-cols-1 3md:grid-cols-2">
                {/* left content */}
                <TabPanel hidden={selectedTab !== "personal"}>
                  <PersonalPanel
                    {...{
                      previewImage,
                      userData,
                      socialMedia,
                      onNext,
                      onBack,
                      onChangePersonalInfo,
                      onChangeHideOccp,
                      onChangeSocialInfo,
                      profile,
                      setPreviewImage
                    }}

                    personalDetails={resumeDetails?.personal_info}
                    details={state.personal}
                    socials={state.social}
                    show_image={state.show_image}
                    resumeId={state.id}
                    templateName = {state.templateName}
                    loadMorePG
                    getProfileGallery
                  />
                </TabPanel>
                <TabPanel hidden={selectedTab !== "more"}>
                  {
                    profile?.profile_type?.type === "group" || profile?.profile_type?.type === "team"
                      ? <MoreInfoPanelTG
                          {...{
                            userData,
                            onNext,
                            onBack,
                            setState,
                            setInitialMount,
                            profile,
                            state
                          }}
                          details={state.more}
                          social={state.social}
                        />
                        : <MoreInfoPanel
                            {...{
                              userData,
                              onNext,
                              onBack,
                              setState,
                              setInitialMount,
                              profile,
                              state
                            }}
                            details={state.more}
                            social={state.social}
                          />
                  }
                </TabPanel>
                <TabPanel hidden={selectedTab !== "milestone"}>
                  <MilestonePanel
                    {...{
                      userData,
                      onNext,
                      onBack,
                      isEdit,
                      onChangeMilestones,
                      onChangeToggleImage,
                      show_image: state.show_image,
                      onChangeToggleImageMilestone,
                      show_image_milestone: state.show_milestone_image,
                      onChangeTogglePrivateMilestone,
                      show_private_milestone: state.show_private_milestone,
                      onChangeHidePosition,
                      milestone_position:state.milestone_position,
                      state
                    }}
                    list={milestones}
                  />
                </TabPanel>
                <TabPanel hidden={selectedTab !== "qrcode"}>
                  <QRCodePanel
                    {...{
                      userData,
                      onNext,
                      onBack,
                      onChangeToggleQRCode,
                      show_qr_milestone: state.show_qr_milestone,
                      show_qr_reels: state.show_qr_reels,
                      hasShowreel: display_qr_reels,
                      state,
                    }}
                  />
                </TabPanel>
                <TabPanel hidden={selectedTab !== "download"}>
                  <ShareDownloadPanel
                    {...{
                      userData,
                      onBack,
                      isSubmit,
                      onDone,
                    }}
                    id={state.link_id || linkId}
                    template={state.templateName}
                  />
                </TabPanel>
                {/* end left content */}
                {/* right content */}
                <div
                  className="w-full py-8 px-0 2sm:px-2 bg-modalGray overflow-auto custom-scroll"
                  style={{ height: "calc(100vh - 80px)" }}>
                  <ResumePreview
                    {...{
                      state,
                      previewImage,
                      // Display-only: drop private milestones unless the toggle is on.
                      // The saved selection (milestones) is untouched. (MCF-1827)
                      milestones: filterPrivateMilestones(milestones, state.show_private_milestone),
                      primaryColor,
                      secondaryColor,
                      tertiaryColor,
                      quarternary,
                      page,
                      setPage,
                      setSelectedTab,
                      profile,
                    }}
                    ref={previewRef}
                  />
                  {
                    selectedTab === 'download' &&
                    <div className="fixed -left-2499.75 top-0 -z-9999">
                      <PreviewPDF
                      {...{
                        state,
                        previewImage,
                        // Display-only private-milestone filter (MCF-1827)
                        milestones: filterPrivateMilestones(milestones, state.show_private_milestone),
                        primaryColor,
                        secondaryColor,
                        tertiaryColor,
                        quarternary,
                        page,
                        setPage,
                        setSelectedTab,
                        profile,
                      }}
                      ref={previewRef}
                    />
                    </div>
                  }
                </div>
                {/* end right contetn */}
              </div>
            </div>
          )}
        </div>
      </div>
    </EmptyLayout>
  );
}

const useQuery = () => {
  const { state } = useLocation();
  return React.useMemo(() => state, [state]);
};
